import { RootState } from "../types";
import {
  PersonalizationReducerState,
  ReducerContainerState,
  AppSettingsState,
} from "./types";
import {
  DEFAULT_TEMPLATES_ON_MODULES,
  EMPTY_LOCALIZED_TEXT,
} from "./constants";
import { DEFAULT_COLORS } from "../../config/constants";
import {
  LangCodeToLocalizedText,
  mapLangCodeToLocalizedTextCode,
} from "../localization/utils";
import { personalizationBooleanPriorityHelper } from "./utils";
import { UserReducer } from "../user/types";
import { getUserSettings } from "../user/selectors";

export const getPersonalizationSlice = (
  state: RootState
): ReducerContainerState => state.personalization;

export const getLocalizedCode = (state: RootState): LangCodeToLocalizedText =>
  mapLangCodeToLocalizedTextCode(state.localization.locale);

export const getServerPersonalizationData = (state: RootState) =>
  getPersonalizationSlice(state).serverPersonalization;

export const getServerPersonalizationMaxDecimalCount = (
  state: RootState
): number =>
  getPersonalizationSlice(state).serverPersonalization?.maxDecimalCount || 0;

export const getTemplatesOnModulesCount = (state: RootState) =>
  getPersonalizationSlice(state).serverPersonalization
    ?.templatesInModulesCount ?? DEFAULT_TEMPLATES_ON_MODULES;

export const getIsPersonalizationFetching = (state: RootState) =>
  getPersonalizationSlice(state).isFetching;

export const getPersonalizationStateChanged = (state: RootState) =>
  getPersonalizationSlice(state).stateChanged;

export const getPersonalizationHasError = (state: RootState) =>
  getPersonalizationSlice(state).hasError;

// Creates one object containing all setting in correct priority
export const getMergedPersonalization = (
  state: RootState
): PersonalizationReducerState => {
  const userSettings = (state.user as UserReducer).settings;
  const { serverPersonalization } = getPersonalizationSlice(state);

  const result: PersonalizationReducerState = {
    activeDirectoryLoginDisabled:
      !!serverPersonalization?.activeDirectoryLoginDisabled,
    appleStoreVisible: personalizationBooleanPriorityHelper(
      serverPersonalization?.appleStoreVisible,
      userSettings.visibleAppStore
    ),
    googleStoreVisible: personalizationBooleanPriorityHelper(
      serverPersonalization?.googleStoreVisible,
      userSettings.visibleGooglePlay
    ),
    homePageTitleVisible: personalizationBooleanPriorityHelper(
      serverPersonalization?.homePageTitleVisible,
      userSettings.visibleTitle
    ),
    homePageInfoVisible: personalizationBooleanPriorityHelper(
      serverPersonalization?.homePageInfoVisible,
      userSettings.visibleDescription
    ),
    googleLoginDisabled: !!serverPersonalization?.googleLoginDisabled,
    microsoftOAuth2LoginDisabled:
      !!serverPersonalization?.microsoftOAuth2LoginDisabled,
    passwordLoginDisabled: !!serverPersonalization?.passwordLoginDisabled,
    homePageTitle: serverPersonalization?.homePageTitle || EMPTY_LOCALIZED_TEXT, // Fallback is comming from BE
    homePageInfo: serverPersonalization?.homePageInfo || EMPTY_LOCALIZED_TEXT, // Fallback is comming from BE
    colors: getServerPersonalizationData(state)?.colors || DEFAULT_COLORS,
    maxDecimalCount: getServerPersonalizationMaxDecimalCount(state),
    templatesInModulesCount:
      serverPersonalization?.templatesInModulesCount ?? 3,
    bugReportVisible: !!serverPersonalization?.bugReportVisible,
    feedbackVisible: !!serverPersonalization?.feedbackVisible,
    versionApplication: serverPersonalization?.versionApplication ?? "",
    keepAuditLogsDays: serverPersonalization?.keepAuditLogsDays ?? 0,
    allowedAuditLogObjects: serverPersonalization?.allowedAuditLogObjects ?? [],
  };

  return result;
};

export const getMergedAppSettingData = (state: RootState): AppSettingsState => {
  const merged = getMergedPersonalization(state);
  const serverData = getServerPersonalizationData(state);
  const personalizationState = getUserSettings(state);

  const isHomePageTitleDisabled = !serverData?.homePageTitleVisible;
  const isHomePageInfoDisabled = !serverData?.homePageInfoVisible;
  const isGoogleStoreDisabled = !serverData?.googleStoreVisible;
  const isAppleStoreDisabled = !serverData?.appleStoreVisible;

  // If admin has disabled the option
  const homePageTitleVisible =
    !isHomePageTitleDisabled && !!personalizationState?.visibleTitle;
  const homePageInfoVisible =
    !isHomePageInfoDisabled && !!personalizationState?.visibleDescription;
  const googleStoreVisible =
    !isGoogleStoreDisabled && !!personalizationState?.visibleGooglePlay;
  const appleStoreVisible =
    !isAppleStoreDisabled && !!personalizationState?.visibleAppStore;

  const { defaultMaxPageRowCount, language, stateChanged } =
    personalizationState;

  return {
    ...merged,
    isHomePageTitleDisabled,
    isHomePageInfoDisabled,
    isGoogleStoreDisabled,
    isAppleStoreDisabled,
    homePageTitleVisible,
    homePageInfoVisible,
    googleStoreVisible,
    appleStoreVisible,
    defaultMaxPageRowCount,
    language,
    stateChanged,
  };
};

export const getPersonalizationDataHomePageTitle = (state: RootState): string =>
  getMergedPersonalization(state).homePageTitle[getLocalizedCode(state)];

export const getPersonalizationDataHomePageInfo = (state: RootState): string =>
  getMergedPersonalization(state).homePageInfo[getLocalizedCode(state)];

// Tohle je takovej hack, bo je to dopraseny jak cyp, celej ten slice je na prepsani...
// Chtel bych brat ze serverPersonalizationData, bo tim je zazdrojovanej ten formular,
// co to meni, ale ten endpoint pro serverPersonalizationData po ulozeni na get posila
// `colors: null`, takze by se mi to zas neprojevilo jindy nez kdyz si to explicitne
// navolim ve formulari...
export const getPersonalizationColors = (state: RootState) =>
  getMergedPersonalization(state).colors;

export const getPrimaryColor = (state: RootState) =>
  getPersonalizationColors(state).primaryColor;

export const getSecondaryColor = (state: RootState) =>
  getPersonalizationColors(state).secondaryColor;

export const getBgColor = (state: RootState) =>
  getPersonalizationColors(state).bgColor;

export const getAppVersion = (state: RootState) =>
  getMergedPersonalization(state).versionApplication;
